import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  Search,
  UserPlus,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Loader2,
  Download,
  Filter,
  Eye,
  Edit3,
  Trash2,
  Upload,
  FileSpreadsheet
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useSubscription, useCanAddMember } from '@/hooks/useSubscription'
import { UpgradePrompt } from '@/components/subscription/UpgradePrompt'

interface Member {
  id: string
  preferred_name: string
  phone_number: string | null
  email: string | null
  role: string
  created_at: string
  church_id: string
  is_active: boolean
  is_verified: boolean
}

export default function MembersNew() {
  const [members, setMembers] = useState<Member[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [churchId, setChurchId] = useState<string | null>(null)
  const { limits, usage } = useSubscription(churchId)
  const { canAdd: canAddMember } = useCanAddMember(churchId)
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false)

  // Action dialogs
  const [viewMember, setViewMember] = useState<Member | null>(null)
  const [editMember, setEditMember] = useState<Member | null>(null)
  const [deleteMember, setDeleteMember] = useState<Member | null>(null)

  // Bulk import
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false)
  const [bulkImportFile, setBulkImportFile] = useState<File | null>(null)
  const [bulkImportData, setBulkImportData] = useState<any[]>([])
  const [bulkImporting, setBulkImporting] = useState(false)
  const [sendSmsToAll, setSendSmsToAll] = useState(true)

  // New member form
  const [newMember, setNewMember] = useState({
    firstName: '',
    lastName: '',
    preferredName: '',
    email: '',
    phone: '',
    address: '',
    role: 'member'
  })

  useEffect(() => {
    loadMembers()
  }, [])

  async function loadMembers() {
    try {
      setLoading(true)

      // Get current user's church
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        console.log('No user found')
        return
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('church_id')
        .eq('id', user.id)
        .single()

      console.log('Admin profile:', profile)

      if (!profile?.church_id) {
        console.log('No church_id found for admin')
        return
      }

      setChurchId(profile.church_id)

      // First, let's try to get ALL members without filtering to see if table exists
      const { data: allMembers, error: allError } = await supabase
        .from('members')
        .select('*')
        .limit(5)

      console.log('All members test (no filter):', { allMembers, allError })

      // Load members from database filtered by church
      const { data, error } = await supabase
        .from('members')
        .select('*')
        .eq('church_id', profile.church_id)
        .order('created_at', { ascending: false })

      console.log('Members query result:', { data, error, church_id: profile.church_id })

      if (error) {
        console.error('Supabase error details:', error)
        throw error
      }

      setMembers(data || [])
    } catch (error) {
      console.error('Error loading members:', error)
    } finally {
      setLoading(false)
    }
  }

  async function handleAddMember(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      const { data: profile } = await supabase
        .from('profiles')
        .select('church_id')
        .eq('id', user.id)
        .single()

      if (!profile?.church_id) {
        alert('No church ID found')
        return
      }

      console.log('🔐 [Admin Dashboard] Creating member via edge function...')

      // Call edge function to create member (has admin privileges)
      const { data: result, error: functionError } = await supabase.functions.invoke('create-member-simple', {
        body: {
          firstName: newMember.firstName,
          lastName: newMember.lastName,
          preferredName: newMember.preferredName || newMember.firstName,
          email: newMember.email || null,
          phone: newMember.phone || null,
          address: newMember.address || null,
          role: newMember.role || 'member',
          churchId: profile.church_id
        }
      })

      if (functionError || !result?.success) {
        console.error('❌ Failed to create member:', functionError || result?.error)
        throw new Error(result?.error || functionError?.message || 'Failed to create member')
      }

      console.log('✅ Member created successfully!', result)

      const { memberId, phone, email, smsSent, emailSent } = result

      // Build notification message
      const notificationMessage = smsSent && emailSent
        ? '📱 Welcome SMS & 📧 Email sent!'
        : smsSent
        ? '📱 Welcome SMS sent!'
        : emailSent
        ? '📧 Welcome Email sent!'
        : 'Member added!'

      alert(`Member added successfully!\n\nMember ID: ${memberId}\nPhone: ${phone}\nEmail: ${email || 'None'}\n\n${notificationMessage}\n\nThe member can now login to the mobile app using their phone number.`)

      // Reset form and reload
      setNewMember({
        firstName: '',
        lastName: '',
        preferredName: '',
        email: '',
        phone: '',
        address: '',
        role: 'member'
      })
      setIsAddDialogOpen(false)
      loadMembers()
    } catch (error: any) {
      console.error('❌ Error adding member:', error)
      alert(error.message || 'Failed to add member')
    } finally {
      setSaving(false)
    }
  }

  // Handle delete member
  const handleDeleteMember = async () => {
    if (!deleteMember) return

    try {
      setSaving(true)

      const { error } = await supabase
        .from('members')
        .delete()
        .eq('id', deleteMember.id)

      if (error) throw error

      alert(`Member ${deleteMember.preferred_name} deleted successfully!`)
      setDeleteMember(null)
      loadMembers()
    } catch (error: any) {
      console.error('❌ Error deleting member:', error)
      alert(error.message || 'Failed to delete member')
    } finally {
      setSaving(false)
    }
  }

  // Handle edit member
  const handleEditMember = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editMember) return

    try {
      setSaving(true)

      const { error } = await supabase
        .from('members')
        .update({
          preferred_name: editMember.preferred_name,
          email: editMember.email,
          phone_number: editMember.phone_number,
          role: editMember.role
        })
        .eq('id', editMember.id)

      if (error) throw error

      alert(`Member ${editMember.preferred_name} updated successfully!`)
      setEditMember(null)
      loadMembers()
    } catch (error: any) {
      console.error('❌ Error updating member:', error)
      alert(error.message || 'Failed to update member')
    } finally {
      setSaving(false)
    }
  }

  // Handle CSV file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setBulkImportFile(file)

    const reader = new FileReader()
    reader.onload = (event) => {
      const text = event.target?.result as string
      parseCSV(text)
    }
    reader.readAsText(file)
  }

  // Parse CSV data
  const parseCSV = (text: string) => {
    const lines = text.split('\n').filter(line => line.trim())
    if (lines.length < 2) {
      alert('CSV file is empty or invalid')
      return
    }

    // Get headers
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase())

    // Parse data rows
    const data = lines.slice(1).map((line, index) => {
      const values = line.split(',').map(v => v.trim())
      const row: any = { rowNumber: index + 2 } // +2 because of header and 0-index

      headers.forEach((header, i) => {
        if (header.includes('first') && header.includes('name')) row.firstName = values[i]
        else if (header.includes('last') && header.includes('name')) row.lastName = values[i]
        else if (header.includes('phone')) row.phone = values[i]
        else if (header.includes('email')) row.email = values[i]
        else if (header.includes('address')) row.address = values[i]
        else if (header.includes('role')) row.role = values[i]
      })

      return row
    }).filter(row => row.firstName && row.lastName) // Must have name

    setBulkImportData(data)
  }

  // Handle bulk import
  const handleBulkImport = async () => {
    if (!bulkImportData.length || !churchId) return

    try {
      setBulkImporting(true)

      let successCount = 0
      let errorCount = 0
      const errors: string[] = []

      for (const member of bulkImportData) {
        try {
          const { data: result, error } = await supabase.functions.invoke('create-member-simple', {
            body: {
              firstName: member.firstName,
              lastName: member.lastName,
              preferredName: member.preferredName || member.firstName,
              email: member.email || null,
              phone: member.phone || null,
              address: member.address || null,
              role: member.role || 'member',
              churchId: churchId
            }
          })

          if (error || !result?.success) {
            errorCount++
            errors.push(`Row ${member.rowNumber}: ${result?.error || error?.message || 'Failed'}`)
          } else {
            successCount++
          }

          // Small delay to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 200))
        } catch (err: any) {
          errorCount++
          errors.push(`Row ${member.rowNumber}: ${err.message}`)
        }
      }

      // Show results
      let message = `✅ Import Complete!\n\n`
      message += `✅ Successfully imported: ${successCount} members\n`
      if (errorCount > 0) {
        message += `❌ Failed: ${errorCount} members\n\n`
        message += `Errors:\n${errors.slice(0, 5).join('\n')}`
        if (errors.length > 5) {
          message += `\n... and ${errors.length - 5} more errors`
        }
      }

      alert(message)

      // Reset and reload
      setIsBulkImportOpen(false)
      setBulkImportFile(null)
      setBulkImportData([])
      loadMembers()
    } catch (error: any) {
      console.error('❌ Bulk import error:', error)
      alert(error.message || 'Failed to import members')
    } finally {
      setBulkImporting(false)
    }
  }

  const filteredMembers = members.filter(member =>
    member.preferred_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.phone_number?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Members</h1>
          <p className="text-muted-foreground">Manage your church membership database</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsBulkImportOpen(true)}
          >
            <Upload className="mr-2 h-4 w-4" />
            Import Members
          </Button>

          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button
                size="sm"
                onClick={() => {
                  if (!canAddMember) {
                    setShowUpgradePrompt(true)
                  } else {
                    setIsAddDialogOpen(true)
                  }
                }}
              >
                <UserPlus className="mr-2 h-4 w-4" />
                Add Member
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] bg-white dark:bg-gray-900">
              <form onSubmit={handleAddMember}>
                <DialogHeader>
                  <DialogTitle>Add New Member</DialogTitle>
                  <DialogDescription>
                    Add a new member to your church database
                  </DialogDescription>
                </DialogHeader>

                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input
                        id="firstName"
                        value={newMember.firstName}
                        onChange={(e) => setNewMember({ ...newMember, firstName: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Input
                        id="lastName"
                        value={newMember.lastName}
                        onChange={(e) => setNewMember({ ...newMember, lastName: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="preferredName">Preferred Name</Label>
                    <Input
                      id="preferredName"
                      value={newMember.preferredName}
                      onChange={(e) => setNewMember({ ...newMember, preferredName: e.target.value })}
                      placeholder="Optional - defaults to first name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={newMember.email}
                      onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={newMember.phone}
                      onChange={(e) => setNewMember({ ...newMember, phone: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      value={newMember.address}
                      onChange={(e) => setNewMember({ ...newMember, address: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <select
                      id="role"
                      value={newMember.role}
                      onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="member">Member</option>
                      <option value="visitor">Visitor</option>
                      <option value="leader">Leader</option>
                    </select>
                  </div>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      'Add Member'
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Members</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{members.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {members.filter(m => m.role === 'member' || m.role === 'leader').length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Visitors</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
              {members.filter(m => m.role === 'visitor').length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Leaders</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {members.filter(m => m.role === 'leader').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search members by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Members Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Members</CardTitle>
          <CardDescription>
            A list of all members in your church
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMembers.length > 0 ? (
                filteredMembers.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell className="font-medium">
                      <div>
                        <p>{member.preferred_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {member.is_verified ? 'Verified' : 'Pending'}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {member.email && (
                          <div className="flex items-center text-sm">
                            <Mail className="mr-2 h-3 w-3" />
                            {member.email}
                          </div>
                        )}
                        {member.phone_number && (
                          <div className="flex items-center text-sm">
                            <Phone className="mr-2 h-3 w-3" />
                            {member.phone_number}
                          </div>
                        )}
                        {!member.email && !member.phone_number && (
                          <span className="text-sm text-muted-foreground">No contact info</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={member.role === 'visitor' ? 'secondary' : 'default'}>
                        {member.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center text-sm">
                        <Calendar className="mr-2 h-3 w-3" />
                        {new Date(member.created_at).toLocaleDateString()}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setViewMember(member)}
                          title="View details"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditMember(member)}
                          title="Edit member"
                        >
                          <Edit3 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteMember(member)}
                          title="Delete member"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    {searchTerm ? 'No members found matching your search' : 'No members yet. Add your first member!'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <UpgradePrompt
        open={showUpgradePrompt}
        onOpenChange={setShowUpgradePrompt}
        limitType="members"
        currentPlan={limits?.plan || 'starter'}
        currentUsage={usage?.current_members_count || 0}
        maxLimit={limits?.max_members || 100}
      />

      {/* View Member Dialog */}
      <Dialog open={!!viewMember} onOpenChange={() => setViewMember(null)}>
        <DialogContent className="sm:max-w-[500px] bg-white dark:bg-gray-900">
          <DialogHeader>
            <DialogTitle>Member Details</DialogTitle>
          </DialogHeader>
          {viewMember && (
            <div className="space-y-4">
              <div>
                <Label className="text-muted-foreground">Name</Label>
                <p className="text-lg font-medium">{viewMember.preferred_name}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Phone</Label>
                <p>{viewMember.phone_number || 'Not provided'}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Email</Label>
                <p>{viewMember.email || 'Not provided'}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Role</Label>
                <p className="capitalize">{viewMember.role}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Status</Label>
                <div className="flex gap-2">
                  <Badge variant={viewMember.is_active ? 'default' : 'secondary'}>
                    {viewMember.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                  <Badge variant={viewMember.is_verified ? 'default' : 'outline'}>
                    {viewMember.is_verified ? 'Verified' : 'Unverified'}
                  </Badge>
                </div>
              </div>
              <div>
                <Label className="text-muted-foreground">Member Since</Label>
                <p>{new Date(viewMember.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setViewMember(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={!!editMember} onOpenChange={() => setEditMember(null)}>
        <DialogContent className="sm:max-w-[500px] bg-white dark:bg-gray-900">
          <form onSubmit={handleEditMember}>
            <DialogHeader>
              <DialogTitle>Edit Member</DialogTitle>
            </DialogHeader>
            {editMember && (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Preferred Name *</Label>
                  <Input
                    id="edit-name"
                    value={editMember.preferred_name}
                    onChange={(e) => setEditMember({ ...editMember, preferred_name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-phone">Phone</Label>
                  <Input
                    id="edit-phone"
                    type="tel"
                    value={editMember.phone_number || ''}
                    onChange={(e) => setEditMember({ ...editMember, phone_number: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-email">Email</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={editMember.email || ''}
                    onChange={(e) => setEditMember({ ...editMember, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-role">Role</Label>
                  <select
                    id="edit-role"
                    value={editMember.role}
                    onChange={(e) => setEditMember({ ...editMember, role: e.target.value })}
                    className="w-full h-10 px-3 border border-input rounded-md"
                  >
                    <option value="member">Member</option>
                    <option value="leader">Leader</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditMember(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Member Dialog */}
      <Dialog open={!!deleteMember} onOpenChange={() => setDeleteMember(null)}>
        <DialogContent className="sm:max-w-[400px] bg-white dark:bg-gray-900">
          <DialogHeader>
            <DialogTitle>Delete Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this member? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteMember && (
            <div className="py-4">
              <p className="font-medium">{deleteMember.preferred_name}</p>
              <p className="text-sm text-muted-foreground">{deleteMember.email || deleteMember.phone_number}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteMember(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteMember} disabled={saving}>
              {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting...</> : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={isBulkImportOpen} onOpenChange={setIsBulkImportOpen}>
        <DialogContent className="sm:max-w-[600px] bg-white dark:bg-gray-900">
          <DialogHeader>
            <DialogTitle>Import Members from CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV file with member information. SMS will be sent to all imported members.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* File upload */}
            {!bulkImportData.length && (
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-8 text-center">
                  <FileSpreadsheet className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <Label htmlFor="csv-upload" className="cursor-pointer">
                    <div className="text-sm font-medium mb-2">Click to upload CSV file</div>
                    <div className="text-xs text-muted-foreground mb-4">
                      File should have headers: First Name, Last Name, Phone, Email, Address
                    </div>
                    <Button type="button" variant="outline" size="sm">
                      Choose File
                    </Button>
                  </Label>
                  <input
                    id="csv-upload"
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>

                {/* CSV Template Download */}
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium mb-2">CSV Format Example:</p>
                  <code className="block bg-gray-100 dark:bg-gray-800 p-3 rounded text-xs">
                    First Name,Last Name,Phone,Email,Address<br/>
                    John,Doe,0241234567,john@email.com,123 Main St<br/>
                    Jane,Smith,0201234567,jane@email.com,456 Oak Ave
                  </code>
                </div>
              </div>
            )}

            {/* Preview data */}
            {bulkImportData.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{bulkImportData.length} members ready to import</p>
                    <p className="text-sm text-muted-foreground">Review the data below before importing</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setBulkImportFile(null)
                      setBulkImportData([])
                    }}
                  >
                    Clear
                  </Button>
                </div>

                {/* Data preview table */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left">#</th>
                          <th className="px-4 py-2 text-left">Name</th>
                          <th className="px-4 py-2 text-left">Phone</th>
                          <th className="px-4 py-2 text-left">Email</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkImportData.map((member, index) => (
                          <tr key={index} className="border-t">
                            <td className="px-4 py-2">{index + 1}</td>
                            <td className="px-4 py-2">{member.firstName} {member.lastName}</td>
                            <td className="px-4 py-2">{member.phone || '-'}</td>
                            <td className="px-4 py-2">{member.email || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* SMS notification option */}
                <div className="flex items-center space-x-2 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                  <input
                    type="checkbox"
                    id="send-sms-bulk"
                    checked={sendSmsToAll}
                    onChange={(e) => setSendSmsToAll(e.target.checked)}
                    className="h-4 w-4"
                  />
                  <label htmlFor="send-sms-bulk" className="text-sm">
                    Send welcome SMS to all imported members (with phone numbers)
                  </label>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsBulkImportOpen(false)
                setBulkImportFile(null)
                setBulkImportData([])
              }}
            >
              Cancel
            </Button>
            {bulkImportData.length > 0 && (
              <Button onClick={handleBulkImport} disabled={bulkImporting}>
                {bulkImporting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Importing {bulkImportData.length} members...
                  </>
                ) : (
                  <>Import {bulkImportData.length} Members</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
